import { Component, computed, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormControl } from '@angular/forms';
import { getPropsFrom, xmlElementToString } from '@lfw/roesberg.liveforms.core';
import { InteractiveItemComponent } from '../../common-items/InteractiveItem/interactive-item-form.component';
import {
  DEFAULT_INTERACTIVE_PROPERTIES,
  InteractiveItemProperties
} from '../../common-items/InteractiveItem/interactive-item-properties';

interface RepeatItemProperties {
  text: string;
  style: string;
  allowCollapse: boolean;
  startOpen: boolean;
  templateItems?: string[];
}

@Component({
  selector: 'lfe-repeat-item-form',
  imports: [CommonModule],
  templateUrl: './repeat-item-form.component.html',
  standalone: true,
  styleUrl: './repeat-item-form.component.css'
})
export class RepeatItemFormComponent extends InteractiveItemComponent<RepeatItemProperties> implements OnInit {
  properties: InteractiveItemProperties & RepeatItemProperties = {
    ...DEFAULT_INTERACTIVE_PROPERTIES,
    text: '',
    style: '',
    allowCollapse: true,
    startOpen: true,
    templateItems: []
  };

  currentId = computed(() => this.properties.customId || this.properties.id);

  htmlId = computed(() => `repeat_${this.currentId()}`);

  ordinals = computed(() => {
    const [firstWord] = this.properties.text.split(' ');
    if (firstWord.endsWith('.')) {
      return firstWord.split('.').filter(Boolean).map(Number);
    }
    return [];
  });

  level = computed(() => Math.max(0, this.ordinals().length - 1));

  repeatFormControl = computed(() => this.form().get(this.currentId()) as FormControl<boolean> | null);

  isOpen = computed(() => {
    const control = this.repeatFormControl();
    return control?.value ?? this.properties.startOpen;
  });

  repeatInstances = signal<string[]>([]);

  repeatArrayControl = computed(() => this.form().get(`${this.currentId()}_instances`) as FormArray | null);

  imageSrc = computed(() => `./assets/FormItems/16/${this.isOpen() ? 'collapseRepeat' : 'expandRepeat'}.png`);

  override getXML(): string {
    return xmlElementToString(this.xml()) || '';

    // return super.getXML();
  }

  override ngOnInit() {
    super.ngOnInit();

    const repeatId = this.currentId();
    if (!this.form().get(repeatId)) {
      this.form().addControl(repeatId, new FormControl(this.properties.startOpen));
    }

    if (!this.form().get(`${repeatId}_instances`)) {
      this.form().addControl(`${repeatId}_instances`, new FormArray([]));
    }
  }

  onToggleClick() {
    if (this.properties.allowCollapse) {
      const control = this.repeatFormControl();
      if (control) {
        control.setValue(!control.value);
      }
    }
  }

  extractLocalProperties(xml: Element) {
    const props = getPropsFrom(xml);

    const customId = props['CustomID'];
    const id = props['ID'];
    const text = props['Text'] || '';
    const style = props['Style'] || '';
    const allowCollapse = props['AllowCollapse'] === 'true' || false;
    const startOpen = props['StartOpen'] === 'true' || true;
    const activationRule = props['ActivationRule'] || '';
    const isGenericRepeatItem = props['IsGenericRepeatItem'] === 'True';
    const resultValue = props['ResultValue'];
    const resultUser = props['ResultUser'];
    const resultTime = props['ResultTime'];

    return {
      ...DEFAULT_INTERACTIVE_PROPERTIES,
      id,
      customId,
      activationRule,
      isGenericRepeatItem,
      text,
      style,
      allowCollapse,
      startOpen,
      resultValue,
      resultUser,
      resultTime,
      templateItems: []
    };
  }
}
